import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import {
  Bell, Search, Menu, X, FileText, Wrench, FolderKanban,
  CreditCard, Shield, AlertTriangle, Calendar, FileSignature
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import LanguageSwitcher from '@/components/common/LanguageSwitcher';
import GlobalSearch from '@/components/layout/GlobalSearch';

const NOTIF_ICONS = {
  estimate_accepted: { icon: FileText, color: 'text-emerald-500', bg: 'bg-emerald-50' },
  estimate_rejected: { icon: FileText, color: 'text-red-500', bg: 'bg-red-50' },
  job_assigned: { icon: Wrench, color: 'text-blue-500', bg: 'bg-blue-50' },
  payment_received: { icon: CreditCard, color: 'text-green-500', bg: 'bg-green-50' },
  permit_approved: { icon: Shield, color: 'text-emerald-500', bg: 'bg-emerald-50' },
  permit_denied: { icon: Shield, color: 'text-red-500', bg: 'bg-red-50' },
  maintenance_due: { icon: AlertTriangle, color: 'text-amber-500', bg: 'bg-amber-50' },
  booking_new: { icon: Calendar, color: 'text-cyan-500', bg: 'bg-cyan-50' },
  schedule_reminder: { icon: Calendar, color: 'text-indigo-500', bg: 'bg-indigo-50' },
  inventory_low: { icon: AlertTriangle, color: 'text-orange-500', bg: 'bg-orange-50' },
  form_signed: { icon: FileSignature, color: 'text-violet-500', bg: 'bg-violet-50' },
};

function useTimeAgo() {
  const { t } = useTranslation();
  return (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return t('common.just_now');
    if (mins < 60) return t('common.minutes_ago', { count: mins });
    const hours = Math.floor(mins / 60);
    if (hours < 24) return t('common.hours_ago', { count: hours });
    const days = Math.floor(hours / 24);
    return t('common.days_ago', { count: days });
  };
}

export default function Header({ onMenuClick }) {
  const { t } = useTranslation();
  const timeAgo = useTimeAgo();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [bellAnimate, setBellAnimate] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  const fetchNotifications = useCallback(async () => {
    try {
      // Fetch ALL notifications (read + unread) — the bell must keep a
      // persistent history in arrival order, not just whatever is unread
      // (Alian: "no se pueden volver a ver", quería el modelo de
      // FieldPulse: se quedan, solo se marcan leidas).
      const [notifsRes, countRes] = await Promise.all([
        api.get('/notifications?page_size=30'),
        api.get('/notifications/unread-count')
      ]);
      const newCount = countRes.data.count;
      if (newCount > unreadCount && unreadCount > 0) {
        setBellAnimate(true);
        setTimeout(() => setBellAnimate(false), 600);
      }
      setNotifications(notifsRes.data?.items || notifsRes.data || []);
      setUnreadCount(newCount);
    } catch { /* ignore */ }
  }, [unreadCount]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markAsRead = async (notif) => {
    // Clicking marks it read AND navigates, but it must stay visible in the
    // history (only the un-read dot disappears) — never removed from the list.
    if (!notif.is_read) {
      try {
        await api.put(`/notifications/${notif.id}/read`);
        setUnreadCount(prev => Math.max(0, prev - 1));
        setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, is_read: true } : n));
      } catch { /* ignore */ }
    }
    // Navigate to the related resource
    const type = notif.related_entity_type;
    const entityId = notif.related_entity_id;
    if (type === 'job') navigate(entityId ? `/jobs/${entityId}` : '/jobs');
    else if (type === 'project') navigate(entityId ? `/projects/${entityId}` : '/projects');
    else if (type === 'estimate') navigate('/estimates', entityId ? { state: { openEstimateId: entityId } } : undefined);
    else if (type === 'invoice') navigate('/invoices', entityId ? { state: { openInvoiceId: entityId, highlightPaymentId: notif.related_entity_meta?.payment_id } } : undefined);
    else if (type === 'permit') navigate(entityId ? `/permits?open=${entityId}` : '/permits');
    else if (type === 'booking') navigate('/bookings');
    else if (type === 'equipment') navigate('/equipment');
    else navigate('/notifications');
  };

  const markAllRead = async () => {
    // The "ojito"/mark-all only flags everything as read — it must NEVER
    // empty the list (Alian: quiere poder scrollear el historial siempre).
    try {
      await api.put('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch { /* ignore */ }
  };

  return (
    <header
      data-testid="app-header"
      className="sticky top-0 z-20 glass border-b border-gray-200/60"
    >
      <div className="flex items-center justify-between h-14 sm:h-16 px-3 sm:px-4 lg:px-8">
        {/* Left: mobile menu + search */}
        <div className="flex items-center gap-2 sm:gap-3 flex-1">
          <Button
            data-testid="mobile-menu-btn"
            variant="ghost"
            size="icon"
            className="lg:hidden text-gray-500 h-9 w-9"
            onClick={onMenuClick}
          >
            <Menu className="w-5 h-5" />
          </Button>
          {/* Desktop search */}
          <div className="hidden sm:flex items-center relative max-w-md flex-1">
            <GlobalSearch testId="global-search" />
          </div>
          {/* Mobile search toggle */}
          <Button
            data-testid="mobile-search-toggle"
            variant="ghost"
            size="icon"
            className="sm:hidden text-gray-500 h-9 w-9"
            onClick={() => setMobileSearchOpen(!mobileSearchOpen)}
          >
            {mobileSearchOpen ? <X className="w-4 h-4" /> : <Search className="w-4 h-4" />}
          </Button>
        </div>

        {/* Right: language + notifications + user */}
        <div className="flex items-center gap-1 sm:gap-2">
          <LanguageSwitcher />
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                data-testid="notification-bell"
                variant="ghost"
                size="icon"
                className={`relative text-gray-500 hover:text-gray-700 ${bellAnimate ? 'animate-bell' : ''}`}
              >
                <Bell className="w-5 h-5" strokeWidth={1.75} />
                {unreadCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center shadow-sm">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80 p-0">
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <h3 className="font-semibold text-sm font-['Barlow']">{t('header.notifications')}</h3>
                {unreadCount > 0 && (
                  <button
                    data-testid="mark-all-read-btn"
                    onClick={markAllRead}
                    className="text-xs text-[#3399FF] hover:underline font-medium"
                  >
                    {t('header.markAllRead')}
                  </button>
                )}
              </div>
              <ScrollArea className="max-h-80">
                {notifications.length === 0 ? (
                  <div className="py-8 text-center text-sm text-gray-400">
                    {t('header.noNotifications')}
                  </div>
                ) : (
                  notifications.map((notif) => {
                    const config = NOTIF_ICONS[notif.type] || { icon: Bell, color: 'text-gray-500', bg: 'bg-gray-50' };
                    const NotifIcon = config.icon;
                    return (
                      <DropdownMenuItem
                        key={notif.id}
                        data-testid={`notification-item-${notif.id}`}
                        onClick={() => markAsRead(notif)}
                        className={`px-4 py-3 cursor-pointer focus:bg-gray-50 gap-3 ${notif.is_read ? 'opacity-60' : ''}`}
                      >
                        <div className={`w-8 h-8 rounded-lg ${config.bg} flex items-center justify-center flex-shrink-0`}>
                          <NotifIcon className={`w-4 h-4 ${config.color}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm truncate ${notif.is_read ? 'font-medium text-gray-600' : 'font-semibold text-gray-900'}`}>{notif.title}</p>
                          <p className="text-xs text-gray-500 truncate">{notif.message}</p>
                          <p className="text-[10px] text-gray-400 mt-0.5">{timeAgo(notif.created_at)}</p>
                        </div>
                        {!notif.is_read && (
                          <div data-testid={`notification-unread-dot-${notif.id}`} className="w-2 h-2 bg-[#3399FF] rounded-full flex-shrink-0" />
                        )}
                      </DropdownMenuItem>
                    );
                  })
                )}
              </ScrollArea>
              {notifications.length > 0 && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    data-testid="view-all-notifications"
                    onClick={() => navigate('/notifications')}
                    className="text-center text-sm text-[#3399FF] font-medium py-2.5 cursor-pointer justify-center"
                  >
                    {t('header.viewAllNotifications')}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* User avatar */}
          <div className="flex items-center gap-2 sm:gap-2.5 ml-1 sm:ml-2 pl-2 sm:pl-3 border-l border-gray-200/60">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#003366] to-[#3399FF] flex items-center justify-center text-white text-xs font-bold shadow-sm">
              {user?.first_name?.[0]}{user?.last_name?.[0]}
            </div>
            <div className="hidden md:block">
              <p className="text-sm font-medium text-gray-800 leading-none">{user?.first_name}</p>
              <p className="text-[10px] text-gray-400 capitalize mt-0.5">{user?.role}</p>
            </div>
          </div>
        </div>
      </div>
      {/* Mobile search bar - expandable */}
      {mobileSearchOpen && (
        <div className="sm:hidden px-3 pb-3 animate-fade-in-up">
          <GlobalSearch testId="mobile-search-input" autoFocus />
        </div>
      )}
    </header>
  );
}
